import { marketSessionPhase } from 'utils/shared/time/marketSession';
import { marketSessionDate } from 'utils/shared/time/marketSessionDate';

/**
 * Free and signed-out viewers get the options tape this many minutes late. The realtime worker's
 * `FREE_DELAY_MS` and the free SQL views both use 30 minutes.
 */
export const FREE_FLOW_DELAY_MINUTES = 30;

export const DELAYED_FLOW_CTA = 'Subscribe for live flow';

/**
 * A free viewer's delayed realtime feed is connected and holds no trades. `sessionDate` is the ET
 * trading date the worker is streaming, or null when the worker has no session yet (a fresh start
 * or a reset). After a reset the worker can start today's date without telling free viewers, so
 * null means "unknown", not "no trades today".
 */
export interface DelayedFlowWait {
  sessionDate: string | null;
}

export interface DelayedFlowNoticeCopy {
  headline: string;
  detail: string;
  /** One line for the market table, which sits directly above the flow table by default. */
  marketLine: string;
}

const DELAY = `Free flow is delayed ${FREE_FLOW_DELAY_MINUTES} minutes.`;
const DETAIL = 'Trades show here once the delay passes. Subscribers see them live.';
const MARKET_LINE = 'Tickers fill in as your delayed trades arrive.';

const COPY: Record<'pre' | 'regular', DelayedFlowNoticeCopy> = {
  // Some symbols trade options from 7:30 AM ET, so today's session can start before the bell.
  pre: { headline: `Today's flow has started. ${DELAY}`, detail: DETAIL, marketLine: MARKET_LINE },
  regular: { headline: `The market is open. ${DELAY}`, detail: DETAIL, marketLine: MARKET_LINE },
};

/**
 * What an empty free feed says, or `null` when an empty table needs no explanation.
 *
 * `sessionDate` is the date of the empty free session. Data decides that today's trading has
 * started: the realtime worker starts a session date only after real trades dated that day arrive.
 * The clock only checks that the date is today and picks the headline: before the 9:30 bell the
 * market is not open yet, so pre-market says that today's flow has started instead. After the
 * close, on weekends and on holidays it says nothing.
 *
 * When the session date is unknown (null), fall back to the clock alone and speak only in the
 * regular session, when an empty free feed is the delay.
 *
 * The copy names no clock time. Early trades reach free viewers 30 minutes after they print, so
 * "your first trades show at 10:00 AM" can be false.
 */
export const delayedFlowNoticeCopy = (
  nowMs: number,
  sessionDate: string | null,
): DelayedFlowNoticeCopy | null => {
  if (!Number.isFinite(nowMs)) return null;
  const phase = marketSessionPhase(nowMs);
  if (sessionDate === null) return phase === 'regular' ? COPY.regular : null;
  if (sessionDate !== marketSessionDate(nowMs)) return null;
  return phase === 'pre' || phase === 'regular' ? COPY[phase] : null;
};
